import React from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import Chip from "@/components/ui/chip";
import { Building, ArrowDown } from "@/components/icons";
import type { TableColumn } from "@/types/component";
import type {
  DepartmentWithPersonnel,
  PersonnelRequestEntry,
  PersonnelRequestStatus,
} from "../types/request-and-approval.types";
import styles from "./DepartmentAccordion.module.scss";

export interface DepartmentAccordionProps {
  departments: DepartmentWithPersonnel[];
  expandedIds: string[];
  onToggleExpand: (id: string) => void;
  selectedRows: PersonnelRequestEntry[];
  onRowSelect: (row: PersonnelRequestEntry) => void;
  onDepartmentSelectAll: (
    department: DepartmentWithPersonnel,
    checked: boolean,
  ) => void;
  talepLabel?: string;
  onayLabel?: string;
  columns?: TableColumn<PersonnelRequestEntry>[];
  selectableStatus?: PersonnelRequestStatus;
}

const getStatusChipType = (
  status: PersonnelRequestStatus,
): "success" | "warning" | "error" => {
  if (status === "onaylandi") return "success";
  if (status === "reddedildi") return "error";
  return "warning";
};

const buildDefaultColumns = (
  t: (key: string) => string,
): TableColumn<PersonnelRequestEntry>[] => {
  const STATUS_LABELS: Record<PersonnelRequestStatus, string> = {
    onaylandi: t("educationOfficer.statusLabels.onaylandi"),
    ilk_onay_bekliyor: t("educationOfficer.statusLabels.ilk_onay_bekliyor"),
    ikinci_onay_bekliyor: t("educationOfficer.statusLabels.ikinci_onay_bekliyor"),
    ucuncu_onay_bekliyor: t("educationOfficer.statusLabels.ucuncu_onay_bekliyor"),
    reddedildi: t("educationOfficer.statusLabels.reddedildi"),
    iptal_edildi: t("educationOfficer.statusLabels.iptal_edildi"),
    tamamlandi: t("educationOfficer.statusLabels.tamamlandi"),
  };
  return [
    {
      header: t("educationOfficer.columns.personnelName"),
      accessor: (row) =>
        row.isBanned ? (
          <span className={styles.bannedCell}>
            <span className={styles.bannedName}>
              {row.personelAdi}{" "}
              <span className={styles.bannedSuffix}>
                ({t("requestAndApproval.blacklisted")})
              </span>
            </span>
          </span>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {row.personelAdi}
          </span>
        ),
      className: "personnel-name-column",
    },
    {
      header: t("educationOfficer.columns.tcKimlikNo"),
      accessor: "tcKimlikNo",
      className: "tc-column",
    },
    {
      header: t("educationOfficer.columns.unvan"),
      accessor: "unvan",
      className: "unvan-column",
    },
    {
      header: t("educationOfficer.columns.status"),
      accessor: (row) => (
        <Chip type={getStatusChipType(row.status)} variant="soft" size="small">
          {STATUS_LABELS[row.status]}
        </Chip>
      ),
      className: "status-column",
    },
  ];
};

const DepartmentAccordion: React.FC<DepartmentAccordionProps> = ({
  departments,
  expandedIds,
  onToggleExpand,
  selectedRows,
  onRowSelect,
  onDepartmentSelectAll,
  talepLabel,
  onayLabel,
  columns: columnsProp,
}) => {
  const { t } = useTranslation();
  const columns = columnsProp || buildDefaultColumns(t);
  const resolvedTalepLabel = talepLabel ?? t("requestAndApproval.shortTalep");
  const resolvedOnayLabel = onayLabel ?? t("requestAndApproval.shortOnay");

  const isRowSelectable = (_row: PersonnelRequestEntry) => true;

  const isDepartmentAllSelected = (dept: DepartmentWithPersonnel) => {
    const selectablePersonnel = dept.personnel.filter(isRowSelectable);
    if (selectablePersonnel.length === 0) return false;
    return selectablePersonnel.every((p) =>
      selectedRows.some((r) => r.id === p.id),
    );
  };

  return (
    <div className={styles.accordion}>
      {departments.map((dept) => {
        const isExpanded = expandedIds.includes(dept.id);
        const isAllSelected = isDepartmentAllSelected(dept);
        return (
          <div
            key={dept.id}
            className={`${styles.accordionItem} ${isExpanded ? styles.expanded : ""}`}
          >
            <div
              className={styles.accordionHeader}
              onClick={() => onToggleExpand(dept.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggleExpand(dept.id);
                }
              }}
              aria-expanded={isExpanded}
            >
              <input
                type="checkbox"
                className={styles.accordionCheckbox}
                checked={isAllSelected}
                disabled={!dept.personnel.some(isRowSelectable)}
                onChange={(e) => {
                  e.stopPropagation();
                  onDepartmentSelectAll(dept, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                aria-label={t("requestAndApproval.selectDeptAria", { name: dept.name })}
              />
              <Building
                width={20}
                height={20}
                className={styles.accordionIcon}
                color="#707a8f"
              />
              <span className={styles.accordionName}>{dept.name}</span>
              <span className={styles.badge}>
                <Chip type="warning" variant="soft" size="small">
                  {dept.talepCount} {resolvedTalepLabel}
                </Chip>
              </span>
              <span className={styles.badge}>
                <Chip type="success" variant="soft" size="small">
                  {dept.onayCount} {resolvedOnayLabel}
                </Chip>
              </span>
              <span
                className={`${styles.accordionChevron} ${isExpanded ? styles.chevronOpen : ""}`}
              >
                <ArrowDown width={20} height={20} color="#707a8f" />
              </span>
            </div>
            <div className={styles.accordionContent}>
              <div className={styles.accordionTableWrap}>
                <Table<PersonnelRequestEntry>
                  columns={columns}
                  data={dept.personnel}
                  selectable
                  isRowSelectable={isRowSelectable}
                  selectedRows={selectedRows}
                  onRowSelect={onRowSelect}
                  onSelectAll={(checked) =>
                    onDepartmentSelectAll(dept, checked)
                  }
                  getRowId={(row) => row.id}
                  emptyMessage={t("educationOfficer.noData")}
                  contentBasedHeight
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DepartmentAccordion;
